import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import type { IFile } from 'src/requests/assets';
import { useDownloadFile } from '../singleton';

const allowedTypes = (mimeType: string): boolean => {
  const type = mimeType?.split('/')[0];
  const isImage = type === 'image';
  const isPdf = mimeType?.toLowerCase().includes('pdf');
  return isImage || isPdf;
};

export const usePrivateFileControl = (
  { _id, name, mimeType }: Pick<IFile, '_id' | 'name' | 'mimeType'>,
  priority?: number,
  showPreview = true,
) => {
  const downloadedStateRef = useRef<boolean>();
  const [objectUrl, setObjectUrl] = useState<string>();

  const {
    isDownloading,
    progress,
    blob,
    queueDownload,
    downloadContent,
    isInQueue,
    dropFromQueue,
  } = useDownloadFile((s) => ({
    isDownloading: s.loadingMap[_id] ?? null,
    progress: s.progressMap[_id] ?? null,
    isInQueue: s.queueMap[_id] ?? false,
    blob: s.contentMap[_id],
    downloadContent: s.downloadContent,
    queueDownload: s.queueDownload,
    dropFromQueue: s.dropFromQueue,
  }));

  const allowPreview = showPreview && allowedTypes(mimeType);

  useEffect(() => {
    if (downloadedStateRef.current !== undefined) return;

    if (!allowPreview) {
      downloadedStateRef.current = false;
      return;
    }

    queueDownload(
      {
        fileId: _id,
        fileName: name,
      },
      priority,
    );
    downloadedStateRef.current = true;

    return () => {
      dropFromQueue(_id);
    };
  }, [allowPreview, _id, name, queueDownload, priority, dropFromQueue]);

  useLayoutEffect(() => {
    if (!blob) return;
    setObjectUrl((s) => {
      if (s) URL.revokeObjectURL(s);
      return URL.createObjectURL(blob);
    });
  }, [blob]);

  useEffect(() => {
    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [objectUrl]);

  const download = useCallback(() => {
    downloadContent({
      fileId: _id,
      fileName: name,
    });
  }, [downloadContent, _id, name]);

  return {
    allowPreview,
    download,
    isLoading: isDownloading,
    isInQueue,
    progress,
    // FileThumbnail file prop
    file: {
      name,
      path: objectUrl,
      preview: objectUrl,
      type: mimeType,
      mimeType,
    } as any,
  };
};
